/**
 * DafDashboardPortal — 25/02/2026
 *
 * Portal público do painel de Cauções (dashboard DAF). Fluxo magic-link:
 *   1. Utilizador abre `/daf-dashboard` (sem token) → botão "Enviar link".
 *   2. Backend cria token de 60 min e envia link para daf@caring.pt.
 *   3. Utilizador clica no email → volta a `/daf-dashboard?token=XXX`.
 *   4. Página valida o token via `/api/public/daf-dashboard/session/{token}`
 *      e depois carrega o painel via
 *      `/api/public/daf-dashboard/deposit-summary/{token}`.
 *   5. Ao expirar (60 min), o painel bloqueia e pede novo link.
 *
 * Não usa `api` (axios com Bearer JWT) — todas as chamadas são anónimas
 * porque o próprio token magic-link é a autenticação.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Mail, ShieldCheck, Clock, Send, LogOut } from "lucide-react";
import DepositSummaryPanel from "@/components/DepositSummaryPanel";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const publicGet = async (path) => {
  const r = await fetch(`${BASE}${path}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

const publicPost = async (path, body = {}) => {
  const r = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

const formatCountdown = (expiresAt) => {
  if (!expiresAt) return "—";
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "Expirada";
  const min = Math.floor(diff / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  return `${min}m ${sec.toString().padStart(2, "0")}s`;
};

export default function DafDashboardPortal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [session, setSession] = useState(null);       // { email, expires_at }
  const [sessionErr, setSessionErr] = useState("");    // "expired" | "invalid" | ""
  const [validating, setValidating] = useState(!!token);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [countdown, setCountdown] = useState("—");

  // 1) Validate incoming token
  useEffect(() => {
    if (!token) { setSession(null); setValidating(false); return; }
    setValidating(true);
    setSessionErr("");
    publicGet(`/api/public/daf-dashboard/session/${token}`)
      .then((s) => setSession(s))
      .catch((e) => {
        // 410 → expired, 404 → invalid/unknown
        const msg = String(e.message || "");
        setSessionErr(msg.includes("410") ? "expired" : "invalid");
        setSession(null);
      })
      .finally(() => setValidating(false));
  }, [token]);

  // 2) Countdown timer for the visible session badge
  useEffect(() => {
    if (!session?.expires_at) return;
    const tick = () => setCountdown(formatCountdown(session.expires_at));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session?.expires_at]);

  const requestAccess = async () => {
    setRequestBusy(true);
    try {
      await publicPost("/api/public/daf-dashboard/request-access", {});
      setRequestSent(true);
    } catch (_e) {
      setRequestSent(true); // never enumerate — always show success message
    } finally {
      setRequestBusy(false);
    }
  };

  // Fetcher que o DepositSummaryPanel usa (magic-link, sem auth)
  const publicFetcher = useMemo(() => {
    if (!token || !session) return null;
    return async () => publicGet(`/api/public/daf-dashboard/deposit-summary/${token}`);
  }, [token, session]);

  const logout = () => {
    setSession(null);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="daf-dashboard-portal">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-foreground/5 border border-border flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-foreground" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                Caring · Portal DAF
              </div>
              <div className="font-ui text-sm">Painel de Cauções</div>
            </div>
          </div>
          {session && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5" data-testid="daf-session-email">
                <Mail className="w-3 h-3" /> {session.email}
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[hsl(var(--warning))] font-semibold flex items-center gap-1.5" data-testid="daf-session-countdown">
                <Clock className="w-3 h-3" /> Sessão termina em {countdown}
              </div>
              <button
                type="button"
                onClick={logout}
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="daf-session-logout"
              >
                <LogOut className="w-3.5 h-3.5" /> Terminar
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
        {/* Loading state */}
        {validating && (
          <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="daf-validating">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> A validar sessão…
          </div>
        )}

        {/* Session active — show the deposit dashboard */}
        {!validating && session && publicFetcher && (
          <DepositSummaryPanel key={token} fetcher={publicFetcher} />
        )}

        {/* No token OR invalid/expired — show request-access card */}
        {!validating && !session && (
          <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="daf-request-access-card">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                Acesso ao painel de Cauções
              </div>
              <h1 className="font-display text-3xl lg:text-4xl tracking-tight">
                Peça o link de acesso
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed pt-1">
                O painel de Cauções está protegido por magic-link. Ao pedir acesso, é enviado
                um link seguro para <b className="text-foreground">daf@caring.pt</b> —
                a sessão fica activa durante <b>60 minutos</b>.
              </p>
            </div>

            {sessionErr === "expired" && (
              <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] p-3 text-xs text-[hsl(var(--warning))]" data-testid="daf-session-expired">
                A sessão expirou. Peça um novo link abaixo.
              </div>
            )}
            {sessionErr === "invalid" && (
              <div className="rounded-xl border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger-bg))] p-3 text-xs text-[hsl(var(--danger))]" data-testid="daf-session-invalid">
                Link inválido. Peça um novo abaixo.
              </div>
            )}
            {requestSent && (
              <div className="rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] p-3 text-xs text-[hsl(var(--success))]" data-testid="daf-request-sent">
                Link enviado para <b>daf@caring.pt</b>. Verifique a caixa de entrada (também vale a pena confirmar a pasta de Spam). O link expira em 60 minutos.
              </div>
            )}

            <button
              type="button"
              onClick={requestAccess}
              disabled={requestBusy}
              data-testid="daf-request-access-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-foreground hover:bg-foreground/90 text-background font-semibold rounded-full px-6 py-2.5 text-sm disabled:opacity-50"
            >
              {requestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {requestSent ? "Reenviar link" : "Enviar link de acesso"}
            </button>

            <div className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-4">
              O link é único e expira automaticamente. Se recebeu um link expirado ou já usado,
              basta pedir um novo — chega ao mesmo endereço.
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-border py-6 text-center text-[11px] text-muted-foreground">
        Caring · Home Care · Este painel é de acesso restrito · sac.qi@caring.pt
      </footer>
    </div>
  );
}
